import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router';

import { AppLockGate } from '@/features/app-lock/ui/AppLockGate';
import { useAuthStore } from '@/features/auth/model/auth-store';
import { watchCalendarSync } from '@/features/calendar-sync/model/sync-calendar';
import { runDailyBackup } from '@/features/data-export/model/auto-backup';
import { startAutoSync } from '@/shared/api/sync-engine';
import { watchHardwareBack } from '@/features/data-export/model/save-backup';
import { runMaintenance } from '@/shared/db/maintenance';
import { loadDiagnostics, watchGlobalErrors } from '@/shared/lib/diagnostics';
import { getCurrentUserId } from '@/shared/db/session-context';
import {
  openDatabase,
  requestPersistentStorage,
  type StorageProblem,
  watchStorage,
} from '@/shared/db/storage-health';

import { router } from './router/routes';
import { ErrorBoundary } from './ui/ErrorBoundary';
import { StorageProblemScreen } from './ui/StorageProblemScreen';

/**
 * Тосты загружаются отдельно: они анимированы, и вместе с ними в первый пакет
 * уезжала вся библиотека анимаций — до кода любого экрана. Первое сообщение
 * появляется в ответ на действие человека, к этому моменту чанк давно на месте.
 */
const ToastViewport = lazy(() =>
  import('@/shared/ui/toast').then((m) => ({ default: m.ToastViewport })),
);

/** Предложение обновиться показывается редко — в первом пакете ему не место. */
const UpdatePrompt = lazy(() =>
  import('@/features/pwa-update/ui/UpdatePrompt').then((m) => ({ default: m.UpdatePrompt })),
);

/**
 * React Query отвечает только за данные из сети (каталоги книг и фильмов,
 * будущая синхронизация). Локальные данные читаются напрямую из IndexedDB
 * через `useLiveQuery` — им кеш поверх базы не нужен.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 2,
      // На мобильном фокус окна теряется постоянно (переключение приложений) —
      // перезапрашивать данные при каждом возврате значит тратить трафик зря.
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const status = useAuthStore((state) => state.status);

  /**
   * Хранилище проверяется до запуска приложения.
   *
   * Если база не открылась, показывать экраны бессмысленно: они будут пустыми,
   * и человек решит, что данные пропали. `null` — проверка ещё идёт, она
   * занимает миллисекунды.
   */
  const [storage, setStorage] = useState<StorageProblem | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const problem = await openDatabase();
      if (cancelled) return;

      setStorage(problem);
      setReady(true);
      if (!problem) void requestPersistentStorage();
    })();

    const unwatch = watchStorage((problem) => setStorage(problem));

    return () => {
      cancelled = true;
      unwatch();
    };
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  /**
   * Аппаратная кнопка «назад» в приложении.
   *
   * Без обработчика она закрывает приложение из любого места: выход из шторки
   * или из раздела означал выход целиком. В браузере вызов ничего не делает.
   */
  useEffect(() => watchHardwareBack(), []);

  /**
   * Глобальные сбои — исключения вне рендера и отклонённые промисы.
   * Границы ошибок их не видят, а следа они иначе не оставляют.
   */
  useEffect(() => {
    void loadDiagnostics();
    return watchGlobalErrors();
  }, []);

  // Синхронизация запускается только после входа: до него нет ни владельца
  // данных, ни токена. Пока сервер не настроен, вызов ничего не делает.
  useEffect(() => {
    if (status !== 'authenticated') return;
    return startAutoSync();
  }, [status]);

  /**
   * Фоновая уборка: раз в сутки убираются давно удалённые записи и сжимается
   * очередь изменений. Без неё база растёт монотонно — удалённое остаётся в тех
   * же индексных диапазонах, что и живое, и приезжает в каждую выборку.
   */
  useEffect(() => {
    if (status !== 'authenticated') return;

    const userId = getCurrentUserId();
    if (!userId) return;

    // С задержкой: запуск приложения важнее уборки.
    const timer = setTimeout(() => void runMaintenance(userId), 5_000);
    return () => clearTimeout(timer);
  }, [status]);

  /**
   * Автоматическая копия при первом за сутки открытии.
   *
   * Резервная копия, которую нужно вспомнить и нажать, — это копия, которой
   * нет. Фоновой выгрузки «пока человек спит» в вебе не существует, зато
   * планировщик открывают почти каждый день: копия, снятая при открытии,
   * оказывается почти всегда свежей.
   *
   * Позже уборки и заметно позже первого экрана: копия — единственная здесь
   * задача, которую человек не ждёт, и отнимать у него первые секунды она не
   * вправе.
   */
  useEffect(() => {
    if (status !== 'authenticated') return;

    const userId = getCurrentUserId();
    if (!userId) return;

    const timer = setTimeout(() => void runDailyBackup(userId), 12_000);
    return () => clearTimeout(timer);
  }, [status]);

  /**
   * Сверка с календарём устройства.
   *
   * Раньше уборки и копии: чужие встречи нужны человеку на первом же экране —
   * пустой день, в котором на самом деле есть планёрка, хуже, чем отсутствие
   * синхронизации вовсе. Пока она не включена в профиле, вызов ничего не делает.
   */
  useEffect(() => {
    if (status !== 'authenticated') return;

    const userId = getCurrentUserId();
    if (!userId) return;

    return watchCalendarSync(userId);
  }, [status]);

  if (storage) return <StorageProblemScreen problem={storage} />;
  if (!ready) return null;

  return (
    <ErrorBoundary area="приложение">
      <QueryClientProvider client={queryClient}>
        {/* Замок стоит выше маршрутизатора: пока заперто, приложения не видно
            вовсе — ни списка дел, ни таблетницы. */}
        <AppLockGate>
          <RouterProvider router={router} />
        </AppLockGate>
        <Suspense fallback={null}>
          <ToastViewport />
        </Suspense>
        <Suspense fallback={null}>
          <UpdatePrompt />
        </Suspense>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
